/**
 * FikoDokumMalzemeDB.ts
 *
 * Casting simulation material database with full thermophysical properties.
 * Covers metal alloys (Al, Fe, Steel, Cu, Mg, Zn) and mold materials
 * (sand, metal die, ceramic, coatings).
 *
 * Every alloy entry carries:
 *   T_solidus, T_liquidus, density (liquid+solid), shrinkage beta,
 *   viscosity mu(T), thermal conductivity k(T), specific heat Cp(T),
 *   latent heat H_f, surface tension sigma, fraction-solid curve f_s(T).
 *
 * Temperature-dependent properties use piecewise-linear lookup tables
 * so the SPH solver can interpolate at any T without curve-fit cost.
 */

// ============================================================================
// 1. ENUM & TEMEL TiPLER
// ============================================================================

export enum DokumAlasimKategori {
  ALUMINYUM   = 'aluminyum',
  DEMIR_DOKUM = 'demir_dokum',
  CELIK_DOKUM = 'celik_dokum',
  BAKIR       = 'bakir',
  MAGNEZYUM   = 'magnezyum',
  CINKO       = 'cinko',
}

export enum KalipMalzemeKategori {
  KUM     = 'kum',
  METAL   = 'metal',
  SERAMIK = 'seramik',
  KAPLAMA = 'kaplama',
}

export enum KatilasmaModeli {
  SCHEIL      = 'scheil',
  LEVER_RULE  = 'lever_rule',
  CUSTOM_POLY = 'custom_poly',
}

/**
 * Single point in a temperature-dependent property curve.
 * Solver performs linear interpolation between consecutive entries.
 */
export interface TSicaklikNokta {
  T: number;
  deger: number;
}

/**
 * Fraction-solid curve point.
 * fs = 0 at T_liquidus, fs = 1 at T_solidus.
 */
export interface KatilasmaNoktasi {
  T: number;
  fs: number;
}

// ============================================================================
// 2. DOKUM ALASIMLARI INTERFACE
// ============================================================================

export interface IDokumAlasim {
  id: string;
  ad: string;
  adEN: string;
  kategori: DokumAlasimKategori;
  standart: string;

  // Phase change temperatures (C)
  T_solidus: number;
  T_liquidus: number;
  T_dokum_min: number;
  T_dokum_max: number;

  // Density
  yogunlukSivi: number;         // kg/m3 at T_liquidus
  yogunlukKati: number;         // kg/m3 at room temp
  cekmeOrani: number;           // beta (volumetric shrinkage fraction, 0-1)

  // Latent heat
  gizliIsi: number;             // J/kg (latent heat of fusion H_f)

  // Surface tension
  yuzeyGerilimi: number;        // N/m at T_liquidus

  // Temperature-dependent viscosity mu(T) [Pa.s]
  // Sorted ascending by T.
  viskoziteEgrisi: TSicaklikNokta[];

  // Temperature-dependent thermal conductivity k(T) [W/(m.K)]
  isiIletkenlikEgrisi: TSicaklikNokta[];

  // Temperature-dependent specific heat Cp(T) [J/(kg.K)]
  ozguIsiEgrisi: TSicaklikNokta[];

  // Fraction-solid curve f_s(T)
  katilasmaModeli: KatilasmaModeli;
  katilasmaEgrisi: KatilasmaNoktasi[];

  // Flow characteristics (casting-specific)
  akiskanlikIndeksi: number;    // 0-100 (fluidity rating)
  sicakYirtilmaEgilimi: number; // 0-100 (hot tearing susceptibility)
  gazCozunurlugu: number;       // cm3/100g (hydrogen solubility etc.)
}

// ============================================================================
// 3. KALIP MALZEMELERi INTERFACE
// ============================================================================

export interface IKalipMalzeme {
  id: string;
  ad: string;
  adEN: string;
  kategori: KalipMalzemeKategori;

  yogunluk: number;            // kg/m3
  isiIletkenlik: number;       // W/(m.K)  (effective / average)
  ozguIsi: number;             // J/(kg.K)
  maxSicaklik: number;         // C  (service temperature limit)

  // Interface heat transfer coefficient h [W/(m2.K)]
  // between melt and mold wall
  arayuzIsiTransferKatsayisi: number;

  // Optional temperature-dependent k(T)
  isiIletkenlikEgrisi?: TSicaklikNokta[];
}

// ============================================================================
// 4. LOOKUP HELPER
// ============================================================================

export function sicaklikLookup(egri: TSicaklikNokta[], T: number): number {
  if (egri.length === 0) return 0;
  if (T <= egri[0].T) return egri[0].deger;
  if (T >= egri[egri.length - 1].T) return egri[egri.length - 1].deger;

  for (let i = 0; i < egri.length - 1; i++) {
    const a = egri[i];
    const b = egri[i + 1];
    if (T >= a.T && T <= b.T) {
      const t = (T - a.T) / (b.T - a.T);
      return a.deger + t * (b.deger - a.deger);
    }
  }
  return egri[egri.length - 1].deger;
}

export function katilasmaFraksiyonu(egri: KatilasmaNoktasi[], T: number): number {
  if (egri.length === 0) return T < 500 ? 1 : 0;
  if (T <= egri[0].T) return egri[0].fs;
  if (T >= egri[egri.length - 1].T) return egri[egri.length - 1].fs;

  for (let i = 0; i < egri.length - 1; i++) {
    const a = egri[i];
    const b = egri[i + 1];
    if (T >= a.T && T <= b.T) {
      const t = (T - a.T) / (b.T - a.T);
      return a.fs + t * (b.fs - a.fs);
    }
  }
  return egri[egri.length - 1].fs;
}

// ============================================================================
// 5. ALUMINYUM ALASIMLARI
// ============================================================================

const AL_A356: IDokumAlasim = {
  id: 'Al_A356',
  ad: 'Aluminyum A356 (AlSi7Mg0.3)',
  adEN: 'Aluminum A356 (AlSi7Mg0.3)',
  kategori: DokumAlasimKategori.ALUMINYUM,
  standart: 'ASTM B108 / EN AC-42100',
  T_solidus: 557,
  T_liquidus: 613,
  T_dokum_min: 680,
  T_dokum_max: 750,
  yogunlukSivi: 2380,
  yogunlukKati: 2680,
  cekmeOrani: 0.068,
  gizliIsi: 389000,
  yuzeyGerilimi: 0.87,
  viskoziteEgrisi: [
    { T: 557, deger: 0.050 },
    { T: 580, deger: 0.012 },
    { T: 613, deger: 0.0028 },
    { T: 700, deger: 0.0012 },
    { T: 800, deger: 0.0009 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 151 },
    { T: 200, deger: 155 },
    { T: 400, deger: 160 },
    { T: 557, deger: 95 },
    { T: 613, deger: 86 },
    { T: 700, deger: 91 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 900 },
    { T: 200, deger: 960 },
    { T: 400, deger: 1040 },
    { T: 557, deger: 1080 },
    { T: 613, deger: 1170 },
    { T: 700, deger: 1180 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 613, fs: 0.0 },
    { T: 600, fs: 0.10 },
    { T: 590, fs: 0.25 },
    { T: 580, fs: 0.45 },
    { T: 572, fs: 0.50 },
    { T: 565, fs: 0.70 },
    { T: 560, fs: 0.85 },
    { T: 557, fs: 1.0 },
  ],
  akiskanlikIndeksi: 92,
  sicakYirtilmaEgilimi: 25,
  gazCozunurlugu: 0.69,
};

const AL_A380: IDokumAlasim = {
  id: 'Al_A380',
  ad: 'Aluminyum A380 (AlSi8Cu3)',
  adEN: 'Aluminum A380 (AlSi8Cu3)',
  kategori: DokumAlasimKategori.ALUMINYUM,
  standart: 'ASTM B85 / EN AC-46000',
  T_solidus: 538,
  T_liquidus: 593,
  T_dokum_min: 640,
  T_dokum_max: 700,
  yogunlukSivi: 2460,
  yogunlukKati: 2710,
  cekmeOrani: 0.060,
  gizliIsi: 389000,
  yuzeyGerilimi: 0.82,
  viskoziteEgrisi: [
    { T: 538, deger: 0.045 },
    { T: 570, deger: 0.010 },
    { T: 593, deger: 0.0025 },
    { T: 680, deger: 0.0011 },
    { T: 750, deger: 0.0008 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 96 },
    { T: 200, deger: 100 },
    { T: 400, deger: 105 },
    { T: 538, deger: 80 },
    { T: 593, deger: 76 },
    { T: 700, deger: 82 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 963 },
    { T: 200, deger: 1000 },
    { T: 400, deger: 1050 },
    { T: 538, deger: 1090 },
    { T: 593, deger: 1170 },
    { T: 700, deger: 1180 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 593, fs: 0.0 },
    { T: 580, fs: 0.12 },
    { T: 570, fs: 0.30 },
    { T: 560, fs: 0.50 },
    { T: 550, fs: 0.68 },
    { T: 542, fs: 0.85 },
    { T: 538, fs: 1.0 },
  ],
  akiskanlikIndeksi: 95,
  sicakYirtilmaEgilimi: 35,
  gazCozunurlugu: 0.65,
};

const AL_A319: IDokumAlasim = {
  id: 'Al_A319',
  ad: 'Aluminyum A319 (AlSi6Cu3)',
  adEN: 'Aluminum A319 (AlSi6Cu3)',
  kategori: DokumAlasimKategori.ALUMINYUM,
  standart: 'ASTM B108 / EN AC-46200',
  T_solidus: 507,
  T_liquidus: 604,
  T_dokum_min: 670,
  T_dokum_max: 740,
  yogunlukSivi: 2430,
  yogunlukKati: 2790,
  cekmeOrani: 0.062,
  gizliIsi: 380000,
  yuzeyGerilimi: 0.84,
  viskoziteEgrisi: [
    { T: 507, deger: 0.055 },
    { T: 560, deger: 0.015 },
    { T: 604, deger: 0.0030 },
    { T: 700, deger: 0.0013 },
    { T: 780, deger: 0.0009 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 109 },
    { T: 200, deger: 115 },
    { T: 400, deger: 118 },
    { T: 507, deger: 85 },
    { T: 604, deger: 80 },
    { T: 700, deger: 85 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 963 },
    { T: 200, deger: 1000 },
    { T: 400, deger: 1050 },
    { T: 507, deger: 1100 },
    { T: 604, deger: 1170 },
    { T: 700, deger: 1180 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 604, fs: 0.0 },
    { T: 590, fs: 0.08 },
    { T: 570, fs: 0.22 },
    { T: 550, fs: 0.42 },
    { T: 530, fs: 0.62 },
    { T: 515, fs: 0.82 },
    { T: 507, fs: 1.0 },
  ],
  akiskanlikIndeksi: 85,
  sicakYirtilmaEgilimi: 40,
  gazCozunurlugu: 0.70,
};

const AL_AlSi12: IDokumAlasim = {
  id: 'Al_AlSi12',
  ad: 'Aluminyum AlSi12 (Etektik)',
  adEN: 'Aluminum AlSi12 (Eutectic)',
  kategori: DokumAlasimKategori.ALUMINYUM,
  standart: 'EN AC-44100',
  T_solidus: 573,
  T_liquidus: 577,
  T_dokum_min: 650,
  T_dokum_max: 720,
  yogunlukSivi: 2410,
  yogunlukKati: 2650,
  cekmeOrani: 0.055,
  gizliIsi: 560000,
  yuzeyGerilimi: 0.83,
  viskoziteEgrisi: [
    { T: 577, deger: 0.0022 },
    { T: 650, deger: 0.0013 },
    { T: 700, deger: 0.0010 },
    { T: 800, deger: 0.0008 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 121 },
    { T: 200, deger: 130 },
    { T: 400, deger: 138 },
    { T: 573, deger: 70 },
    { T: 577, deger: 68 },
    { T: 700, deger: 75 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 880 },
    { T: 200, deger: 930 },
    { T: 400, deger: 1000 },
    { T: 573, deger: 1060 },
    { T: 577, deger: 1150 },
    { T: 700, deger: 1160 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 577, fs: 0.0 },
    { T: 576, fs: 0.40 },
    { T: 575, fs: 0.70 },
    { T: 574, fs: 0.90 },
    { T: 573, fs: 1.0 },
  ],
  akiskanlikIndeksi: 98,
  sicakYirtilmaEgilimi: 10,
  gazCozunurlugu: 0.68,
};

// ============================================================================
// 6. DEMiR DOKUM ALASIMLARI
// ============================================================================

const GG25: IDokumAlasim = {
  id: 'GG25',
  ad: 'Gri Dokum GG25 (GJL-250)',
  adEN: 'Gray Cast Iron GG25 (EN-GJL-250)',
  kategori: DokumAlasimKategori.DEMIR_DOKUM,
  standart: 'EN 1561 / DIN 1691',
  T_solidus: 1140,
  T_liquidus: 1230,
  T_dokum_min: 1320,
  T_dokum_max: 1420,
  yogunlukSivi: 6800,
  yogunlukKati: 7200,
  cekmeOrani: 0.017,
  gizliIsi: 230000,
  yuzeyGerilimi: 1.10,
  viskoziteEgrisi: [
    { T: 1140, deger: 0.080 },
    { T: 1200, deger: 0.020 },
    { T: 1230, deger: 0.0060 },
    { T: 1350, deger: 0.0040 },
    { T: 1450, deger: 0.0030 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 46 },
    { T: 200,  deger: 44 },
    { T: 400,  deger: 41 },
    { T: 600,  deger: 37 },
    { T: 800,  deger: 33 },
    { T: 1140, deger: 28 },
    { T: 1230, deger: 20 },
    { T: 1400, deger: 22 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 460 },
    { T: 200,  deger: 500 },
    { T: 400,  deger: 540 },
    { T: 600,  deger: 590 },
    { T: 800,  deger: 650 },
    { T: 1140, deger: 750 },
    { T: 1230, deger: 830 },
    { T: 1400, deger: 840 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1230, fs: 0.0 },
    { T: 1210, fs: 0.08 },
    { T: 1190, fs: 0.20 },
    { T: 1170, fs: 0.45 },
    { T: 1155, fs: 0.65 },
    { T: 1147, fs: 0.82 },
    { T: 1140, fs: 1.0 },
  ],
  akiskanlikIndeksi: 85,
  sicakYirtilmaEgilimi: 15,
  gazCozunurlugu: 0.25,
};

const GGG40: IDokumAlasim = {
  id: 'GGG40',
  ad: 'Sfero GGG40 (GJS-400-18)',
  adEN: 'Ductile Iron GGG40 (EN-GJS-400-18)',
  kategori: DokumAlasimKategori.DEMIR_DOKUM,
  standart: 'EN 1563 / DIN 1693',
  T_solidus: 1130,
  T_liquidus: 1180,
  T_dokum_min: 1300,
  T_dokum_max: 1400,
  yogunlukSivi: 6920,
  yogunlukKati: 7100,
  cekmeOrani: 0.042,
  gizliIsi: 230000,
  yuzeyGerilimi: 1.40,
  viskoziteEgrisi: [
    { T: 1130, deger: 0.060 },
    { T: 1160, deger: 0.018 },
    { T: 1180, deger: 0.0055 },
    { T: 1300, deger: 0.0042 },
    { T: 1400, deger: 0.0032 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 36 },
    { T: 200,  deger: 35 },
    { T: 400,  deger: 33 },
    { T: 600,  deger: 31 },
    { T: 800,  deger: 29 },
    { T: 1130, deger: 24 },
    { T: 1180, deger: 18 },
    { T: 1400, deger: 20 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 470 },
    { T: 200,  deger: 510 },
    { T: 400,  deger: 555 },
    { T: 600,  deger: 610 },
    { T: 800,  deger: 680 },
    { T: 1130, deger: 760 },
    { T: 1180, deger: 840 },
    { T: 1400, deger: 850 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1180, fs: 0.0 },
    { T: 1170, fs: 0.15 },
    { T: 1160, fs: 0.35 },
    { T: 1150, fs: 0.55 },
    { T: 1140, fs: 0.75 },
    { T: 1135, fs: 0.90 },
    { T: 1130, fs: 1.0 },
  ],
  akiskanlikIndeksi: 75,
  sicakYirtilmaEgilimi: 30,
  gazCozunurlugu: 0.22,
};

const GGG60: IDokumAlasim = {
  id: 'GGG60',
  ad: 'Sfero GGG60 (GJS-600-3)',
  adEN: 'Ductile Iron GGG60 (EN-GJS-600-3)',
  kategori: DokumAlasimKategori.DEMIR_DOKUM,
  standart: 'EN 1563 / DIN 1693',
  T_solidus: 1130,
  T_liquidus: 1175,
  T_dokum_min: 1310,
  T_dokum_max: 1410,
  yogunlukSivi: 6950,
  yogunlukKati: 7100,
  cekmeOrani: 0.048,
  gizliIsi: 226000,
  yuzeyGerilimi: 1.42,
  viskoziteEgrisi: [
    { T: 1130, deger: 0.065 },
    { T: 1155, deger: 0.020 },
    { T: 1175, deger: 0.0058 },
    { T: 1300, deger: 0.0044 },
    { T: 1400, deger: 0.0034 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 32 },
    { T: 200,  deger: 31 },
    { T: 400,  deger: 30 },
    { T: 600,  deger: 28 },
    { T: 800,  deger: 26 },
    { T: 1130, deger: 22 },
    { T: 1175, deger: 17 },
    { T: 1400, deger: 19 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 475 },
    { T: 200,  deger: 515 },
    { T: 400,  deger: 560 },
    { T: 600,  deger: 620 },
    { T: 800,  deger: 690 },
    { T: 1130, deger: 770 },
    { T: 1175, deger: 845 },
    { T: 1400, deger: 855 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1175, fs: 0.0 },
    { T: 1165, fs: 0.12 },
    { T: 1155, fs: 0.32 },
    { T: 1145, fs: 0.55 },
    { T: 1138, fs: 0.75 },
    { T: 1133, fs: 0.92 },
    { T: 1130, fs: 1.0 },
  ],
  akiskanlikIndeksi: 72,
  sicakYirtilmaEgilimi: 35,
  gazCozunurlugu: 0.22,
};

const VERMICULAR: IDokumAlasim = {
  id: 'GJV_450',
  ad: 'Vermikuler (GJV-450)',
  adEN: 'Compacted Graphite Iron (GJV-450)',
  kategori: DokumAlasimKategori.DEMIR_DOKUM,
  standart: 'EN 16079',
  T_solidus: 1130,
  T_liquidus: 1185,
  T_dokum_min: 1320,
  T_dokum_max: 1420,
  yogunlukSivi: 6900,
  yogunlukKati: 7100,
  cekmeOrani: 0.035,
  gizliIsi: 228000,
  yuzeyGerilimi: 1.30,
  viskoziteEgrisi: [
    { T: 1130, deger: 0.070 },
    { T: 1160, deger: 0.019 },
    { T: 1185, deger: 0.0056 },
    { T: 1320, deger: 0.0041 },
    { T: 1420, deger: 0.0031 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 38 },
    { T: 200,  deger: 37 },
    { T: 400,  deger: 35 },
    { T: 600,  deger: 33 },
    { T: 800,  deger: 30 },
    { T: 1130, deger: 25 },
    { T: 1185, deger: 19 },
    { T: 1420, deger: 21 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 465 },
    { T: 200,  deger: 505 },
    { T: 400,  deger: 550 },
    { T: 600,  deger: 605 },
    { T: 800,  deger: 665 },
    { T: 1130, deger: 755 },
    { T: 1185, deger: 835 },
    { T: 1420, deger: 845 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1185, fs: 0.0 },
    { T: 1175, fs: 0.10 },
    { T: 1160, fs: 0.28 },
    { T: 1150, fs: 0.48 },
    { T: 1140, fs: 0.70 },
    { T: 1135, fs: 0.88 },
    { T: 1130, fs: 1.0 },
  ],
  akiskanlikIndeksi: 78,
  sicakYirtilmaEgilimi: 22,
  gazCozunurlugu: 0.24,
};

// ============================================================================
// 7. CELiK DOKUM ALASIMLARI
// ============================================================================

const GP240GH: IDokumAlasim = {
  id: 'GP240GH',
  ad: 'Celik Dokum GP240GH',
  adEN: 'Cast Steel GP240GH',
  kategori: DokumAlasimKategori.CELIK_DOKUM,
  standart: 'EN 10213',
  T_solidus: 1420,
  T_liquidus: 1510,
  T_dokum_min: 1560,
  T_dokum_max: 1620,
  yogunlukSivi: 7000,
  yogunlukKati: 7850,
  cekmeOrani: 0.072,
  gizliIsi: 272000,
  yuzeyGerilimi: 1.80,
  viskoziteEgrisi: [
    { T: 1420, deger: 0.080 },
    { T: 1470, deger: 0.020 },
    { T: 1510, deger: 0.0065 },
    { T: 1600, deger: 0.0050 },
    { T: 1650, deger: 0.0042 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 50 },
    { T: 200,  deger: 48 },
    { T: 400,  deger: 44 },
    { T: 600,  deger: 38 },
    { T: 800,  deger: 30 },
    { T: 1000, deger: 27 },
    { T: 1420, deger: 25 },
    { T: 1510, deger: 28 },
    { T: 1620, deger: 30 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 460 },
    { T: 200,  deger: 500 },
    { T: 400,  deger: 545 },
    { T: 600,  deger: 600 },
    { T: 800,  deger: 700 },
    { T: 1000, deger: 750 },
    { T: 1420, deger: 780 },
    { T: 1510, deger: 820 },
    { T: 1620, deger: 830 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 1510, fs: 0.0 },
    { T: 1495, fs: 0.12 },
    { T: 1475, fs: 0.30 },
    { T: 1455, fs: 0.52 },
    { T: 1440, fs: 0.72 },
    { T: 1428, fs: 0.90 },
    { T: 1420, fs: 1.0 },
  ],
  akiskanlikIndeksi: 50,
  sicakYirtilmaEgilimi: 55,
  gazCozunurlugu: 0.30,
};

const G17CrMo55: IDokumAlasim = {
  id: 'G17CrMo5_5',
  ad: 'Celik Dokum G17CrMo5-5',
  adEN: 'Cast Steel G17CrMo5-5',
  kategori: DokumAlasimKategori.CELIK_DOKUM,
  standart: 'EN 10213',
  T_solidus: 1390,
  T_liquidus: 1490,
  T_dokum_min: 1550,
  T_dokum_max: 1610,
  yogunlukSivi: 7050,
  yogunlukKati: 7850,
  cekmeOrani: 0.075,
  gizliIsi: 268000,
  yuzeyGerilimi: 1.78,
  viskoziteEgrisi: [
    { T: 1390, deger: 0.085 },
    { T: 1440, deger: 0.022 },
    { T: 1490, deger: 0.0068 },
    { T: 1580, deger: 0.0052 },
    { T: 1650, deger: 0.0043 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 42 },
    { T: 200,  deger: 41 },
    { T: 400,  deger: 38 },
    { T: 600,  deger: 34 },
    { T: 800,  deger: 28 },
    { T: 1000, deger: 26 },
    { T: 1390, deger: 24 },
    { T: 1490, deger: 27 },
    { T: 1610, deger: 29 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 460 },
    { T: 200,  deger: 505 },
    { T: 400,  deger: 555 },
    { T: 600,  deger: 620 },
    { T: 800,  deger: 720 },
    { T: 1000, deger: 760 },
    { T: 1390, deger: 790 },
    { T: 1490, deger: 825 },
    { T: 1610, deger: 835 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 1490, fs: 0.0 },
    { T: 1475, fs: 0.10 },
    { T: 1455, fs: 0.28 },
    { T: 1435, fs: 0.50 },
    { T: 1415, fs: 0.72 },
    { T: 1400, fs: 0.90 },
    { T: 1390, fs: 1.0 },
  ],
  akiskanlikIndeksi: 45,
  sicakYirtilmaEgilimi: 60,
  gazCozunurlugu: 0.28,
};

const CF8M: IDokumAlasim = {
  id: 'CF8M',
  ad: 'Paslanmaz Dokum CF8M (316)',
  adEN: 'Stainless Cast CF8M (316 equiv.)',
  kategori: DokumAlasimKategori.CELIK_DOKUM,
  standart: 'ASTM A351',
  T_solidus: 1375,
  T_liquidus: 1430,
  T_dokum_min: 1500,
  T_dokum_max: 1580,
  yogunlukSivi: 7000,
  yogunlukKati: 7960,
  cekmeOrani: 0.065,
  gizliIsi: 260000,
  yuzeyGerilimi: 1.75,
  viskoziteEgrisi: [
    { T: 1375, deger: 0.070 },
    { T: 1410, deger: 0.018 },
    { T: 1430, deger: 0.0060 },
    { T: 1520, deger: 0.0048 },
    { T: 1600, deger: 0.0040 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 14 },
    { T: 200,  deger: 16 },
    { T: 400,  deger: 18 },
    { T: 600,  deger: 21 },
    { T: 800,  deger: 24 },
    { T: 1000, deger: 26 },
    { T: 1375, deger: 28 },
    { T: 1430, deger: 30 },
    { T: 1580, deger: 32 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 500 },
    { T: 200,  deger: 530 },
    { T: 400,  deger: 560 },
    { T: 600,  deger: 590 },
    { T: 800,  deger: 630 },
    { T: 1000, deger: 670 },
    { T: 1375, deger: 720 },
    { T: 1430, deger: 780 },
    { T: 1580, deger: 790 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1430, fs: 0.0 },
    { T: 1420, fs: 0.10 },
    { T: 1410, fs: 0.25 },
    { T: 1400, fs: 0.45 },
    { T: 1390, fs: 0.65 },
    { T: 1382, fs: 0.85 },
    { T: 1375, fs: 1.0 },
  ],
  akiskanlikIndeksi: 42,
  sicakYirtilmaEgilimi: 50,
  gazCozunurlugu: 0.18,
};

// ============================================================================
// 8. BAKIR ALASIMLARI
// ============================================================================

const CuSn10: IDokumAlasim = {
  id: 'CuSn10',
  ad: 'Kalay Bronz CuSn10',
  adEN: 'Tin Bronze CuSn10 (C90700)',
  kategori: DokumAlasimKategori.BAKIR,
  standart: 'EN 1982 CC480K / ASTM B22',
  T_solidus: 850,
  T_liquidus: 1000,
  T_dokum_min: 1100,
  T_dokum_max: 1200,
  yogunlukSivi: 7900,
  yogunlukKati: 8780,
  cekmeOrani: 0.050,
  gizliIsi: 205000,
  yuzeyGerilimi: 1.10,
  viskoziteEgrisi: [
    { T: 850,  deger: 0.060 },
    { T: 930,  deger: 0.015 },
    { T: 1000, deger: 0.0040 },
    { T: 1100, deger: 0.0030 },
    { T: 1200, deger: 0.0024 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 50 },
    { T: 200,  deger: 52 },
    { T: 400,  deger: 55 },
    { T: 600,  deger: 58 },
    { T: 850,  deger: 45 },
    { T: 1000, deger: 40 },
    { T: 1200, deger: 42 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 380 },
    { T: 200,  deger: 395 },
    { T: 400,  deger: 415 },
    { T: 600,  deger: 440 },
    { T: 850,  deger: 480 },
    { T: 1000, deger: 510 },
    { T: 1200, deger: 520 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 1000, fs: 0.0 },
    { T: 980,  fs: 0.08 },
    { T: 950,  fs: 0.22 },
    { T: 920,  fs: 0.40 },
    { T: 890,  fs: 0.60 },
    { T: 870,  fs: 0.82 },
    { T: 850,  fs: 1.0 },
  ],
  akiskanlikIndeksi: 72,
  sicakYirtilmaEgilimi: 45,
  gazCozunurlugu: 0.08,
};

const CuZn_PIRINC: IDokumAlasim = {
  id: 'CuZn33',
  ad: 'Pirinc CuZn33 (Kirmizi Pirinc)',
  adEN: 'Brass CuZn33 (Red Brass C83600)',
  kategori: DokumAlasimKategori.BAKIR,
  standart: 'EN 1982 CC491K / ASTM B584',
  T_solidus: 855,
  T_liquidus: 1010,
  T_dokum_min: 1060,
  T_dokum_max: 1150,
  yogunlukSivi: 7850,
  yogunlukKati: 8700,
  cekmeOrani: 0.045,
  gizliIsi: 200000,
  yuzeyGerilimi: 1.00,
  viskoziteEgrisi: [
    { T: 855,  deger: 0.055 },
    { T: 940,  deger: 0.013 },
    { T: 1010, deger: 0.0035 },
    { T: 1100, deger: 0.0028 },
    { T: 1200, deger: 0.0022 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 75 },
    { T: 200,  deger: 78 },
    { T: 400,  deger: 80 },
    { T: 600,  deger: 70 },
    { T: 855,  deger: 55 },
    { T: 1010, deger: 50 },
    { T: 1150, deger: 52 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 380 },
    { T: 200,  deger: 390 },
    { T: 400,  deger: 410 },
    { T: 600,  deger: 430 },
    { T: 855,  deger: 470 },
    { T: 1010, deger: 500 },
    { T: 1150, deger: 510 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 1010, fs: 0.0 },
    { T: 985,  fs: 0.10 },
    { T: 955,  fs: 0.25 },
    { T: 925,  fs: 0.45 },
    { T: 895,  fs: 0.65 },
    { T: 870,  fs: 0.85 },
    { T: 855,  fs: 1.0 },
  ],
  akiskanlikIndeksi: 78,
  sicakYirtilmaEgilimi: 35,
  gazCozunurlugu: 0.05,
};

const CuAl_BRONZ: IDokumAlasim = {
  id: 'CuAl10Ni',
  ad: 'Aluminyum Bronz CuAl10Fe5Ni5',
  adEN: 'Aluminum Bronze CuAl10Fe5Ni5 (C95800)',
  kategori: DokumAlasimKategori.BAKIR,
  standart: 'EN 1982 CC333G / ASTM B148',
  T_solidus: 1015,
  T_liquidus: 1060,
  T_dokum_min: 1150,
  T_dokum_max: 1260,
  yogunlukSivi: 7200,
  yogunlukKati: 7640,
  cekmeOrani: 0.042,
  gizliIsi: 210000,
  yuzeyGerilimi: 1.20,
  viskoziteEgrisi: [
    { T: 1015, deger: 0.058 },
    { T: 1040, deger: 0.015 },
    { T: 1060, deger: 0.0042 },
    { T: 1150, deger: 0.0032 },
    { T: 1260, deger: 0.0026 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 44 },
    { T: 200,  deger: 46 },
    { T: 400,  deger: 48 },
    { T: 600,  deger: 50 },
    { T: 800,  deger: 46 },
    { T: 1015, deger: 38 },
    { T: 1060, deger: 35 },
    { T: 1260, deger: 37 },
  ],
  ozguIsiEgrisi: [
    { T: 25,   deger: 420 },
    { T: 200,  deger: 440 },
    { T: 400,  deger: 460 },
    { T: 600,  deger: 485 },
    { T: 800,  deger: 510 },
    { T: 1015, deger: 540 },
    { T: 1060, deger: 565 },
    { T: 1260, deger: 575 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 1060, fs: 0.0 },
    { T: 1050, fs: 0.15 },
    { T: 1040, fs: 0.35 },
    { T: 1030, fs: 0.55 },
    { T: 1022, fs: 0.75 },
    { T: 1018, fs: 0.92 },
    { T: 1015, fs: 1.0 },
  ],
  akiskanlikIndeksi: 65,
  sicakYirtilmaEgilimi: 40,
  gazCozunurlugu: 0.06,
};

// ============================================================================
// 9. MAGNEZYUM ALASIMLARI
// ============================================================================

const AZ91: IDokumAlasim = {
  id: 'Mg_AZ91',
  ad: 'Magnezyum AZ91D',
  adEN: 'Magnesium AZ91D',
  kategori: DokumAlasimKategori.MAGNEZYUM,
  standart: 'ASTM B94',
  T_solidus: 470,
  T_liquidus: 595,
  T_dokum_min: 640,
  T_dokum_max: 710,
  yogunlukSivi: 1580,
  yogunlukKati: 1810,
  cekmeOrani: 0.042,
  gizliIsi: 373000,
  yuzeyGerilimi: 0.56,
  viskoziteEgrisi: [
    { T: 470, deger: 0.040 },
    { T: 540, deger: 0.010 },
    { T: 595, deger: 0.0025 },
    { T: 680, deger: 0.0012 },
    { T: 750, deger: 0.0009 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 72 },
    { T: 200, deger: 78 },
    { T: 400, deger: 82 },
    { T: 470, deger: 60 },
    { T: 595, deger: 55 },
    { T: 700, deger: 58 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 1020 },
    { T: 200, deger: 1060 },
    { T: 400, deger: 1120 },
    { T: 470, deger: 1150 },
    { T: 595, deger: 1250 },
    { T: 700, deger: 1260 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 595, fs: 0.0 },
    { T: 575, fs: 0.10 },
    { T: 550, fs: 0.28 },
    { T: 525, fs: 0.48 },
    { T: 500, fs: 0.68 },
    { T: 480, fs: 0.88 },
    { T: 470, fs: 1.0 },
  ],
  akiskanlikIndeksi: 88,
  sicakYirtilmaEgilimi: 45,
  gazCozunurlugu: 0.30,
};

const AM60: IDokumAlasim = {
  id: 'Mg_AM60',
  ad: 'Magnezyum AM60B',
  adEN: 'Magnesium AM60B',
  kategori: DokumAlasimKategori.MAGNEZYUM,
  standart: 'ASTM B94',
  T_solidus: 540,
  T_liquidus: 615,
  T_dokum_min: 660,
  T_dokum_max: 720,
  yogunlukSivi: 1570,
  yogunlukKati: 1790,
  cekmeOrani: 0.038,
  gizliIsi: 370000,
  yuzeyGerilimi: 0.55,
  viskoziteEgrisi: [
    { T: 540, deger: 0.038 },
    { T: 580, deger: 0.010 },
    { T: 615, deger: 0.0023 },
    { T: 700, deger: 0.0011 },
    { T: 760, deger: 0.0008 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 61 },
    { T: 200, deger: 66 },
    { T: 400, deger: 70 },
    { T: 540, deger: 52 },
    { T: 615, deger: 48 },
    { T: 720, deger: 50 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 1020 },
    { T: 200, deger: 1060 },
    { T: 400, deger: 1110 },
    { T: 540, deger: 1140 },
    { T: 615, deger: 1240 },
    { T: 720, deger: 1250 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 615, fs: 0.0 },
    { T: 600, fs: 0.12 },
    { T: 585, fs: 0.30 },
    { T: 570, fs: 0.50 },
    { T: 555, fs: 0.72 },
    { T: 545, fs: 0.90 },
    { T: 540, fs: 1.0 },
  ],
  akiskanlikIndeksi: 85,
  sicakYirtilmaEgilimi: 50,
  gazCozunurlugu: 0.28,
};

// ============================================================================
// 10. CiNKO ALASIMLARI
// ============================================================================

const ZAMAK3: IDokumAlasim = {
  id: 'Zn_Zamak3',
  ad: 'Zamak 3 (ZnAl4)',
  adEN: 'Zamak 3 (ASTM AG40A)',
  kategori: DokumAlasimKategori.CINKO,
  standart: 'EN 12844 ZL0400 / ASTM B86',
  T_solidus: 381,
  T_liquidus: 387,
  T_dokum_min: 400,
  T_dokum_max: 430,
  yogunlukSivi: 6200,
  yogunlukKati: 6600,
  cekmeOrani: 0.010,
  gizliIsi: 113000,
  yuzeyGerilimi: 0.78,
  viskoziteEgrisi: [
    { T: 387, deger: 0.0035 },
    { T: 410, deger: 0.0028 },
    { T: 440, deger: 0.0022 },
    { T: 480, deger: 0.0018 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 113 },
    { T: 200, deger: 105 },
    { T: 381, deger: 90 },
    { T: 387, deger: 55 },
    { T: 430, deger: 58 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 418 },
    { T: 200, deger: 440 },
    { T: 381, deger: 470 },
    { T: 387, deger: 510 },
    { T: 430, deger: 515 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 387, fs: 0.0 },
    { T: 386, fs: 0.25 },
    { T: 384, fs: 0.55 },
    { T: 382, fs: 0.80 },
    { T: 381, fs: 1.0 },
  ],
  akiskanlikIndeksi: 98,
  sicakYirtilmaEgilimi: 8,
  gazCozunurlugu: 0.02,
};

const ZAMAK5: IDokumAlasim = {
  id: 'Zn_Zamak5',
  ad: 'Zamak 5 (ZnAl4Cu1)',
  adEN: 'Zamak 5 (ASTM AC41A)',
  kategori: DokumAlasimKategori.CINKO,
  standart: 'EN 12844 ZL0410 / ASTM B86',
  T_solidus: 380,
  T_liquidus: 386,
  T_dokum_min: 400,
  T_dokum_max: 435,
  yogunlukSivi: 6250,
  yogunlukKati: 6700,
  cekmeOrani: 0.012,
  gizliIsi: 111000,
  yuzeyGerilimi: 0.76,
  viskoziteEgrisi: [
    { T: 386, deger: 0.0038 },
    { T: 410, deger: 0.0029 },
    { T: 440, deger: 0.0023 },
    { T: 480, deger: 0.0019 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 109 },
    { T: 200, deger: 101 },
    { T: 380, deger: 87 },
    { T: 386, deger: 53 },
    { T: 435, deger: 56 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 418 },
    { T: 200, deger: 438 },
    { T: 380, deger: 468 },
    { T: 386, deger: 508 },
    { T: 435, deger: 513 },
  ],
  katilasmaModeli: KatilasmaModeli.LEVER_RULE,
  katilasmaEgrisi: [
    { T: 386, fs: 0.0 },
    { T: 385, fs: 0.22 },
    { T: 383, fs: 0.52 },
    { T: 381, fs: 0.78 },
    { T: 380, fs: 1.0 },
  ],
  akiskanlikIndeksi: 97,
  sicakYirtilmaEgilimi: 10,
  gazCozunurlugu: 0.02,
};

const ZA8: IDokumAlasim = {
  id: 'Zn_ZA8',
  ad: 'Cinko ZA-8 (ZnAl8Cu1)',
  adEN: 'Zinc ZA-8 (ZnAl8Cu1)',
  kategori: DokumAlasimKategori.CINKO,
  standart: 'ASTM B669',
  T_solidus: 375,
  T_liquidus: 404,
  T_dokum_min: 440,
  T_dokum_max: 480,
  yogunlukSivi: 6020,
  yogunlukKati: 6300,
  cekmeOrani: 0.018,
  gizliIsi: 120000,
  yuzeyGerilimi: 0.74,
  viskoziteEgrisi: [
    { T: 404, deger: 0.0040 },
    { T: 430, deger: 0.0030 },
    { T: 460, deger: 0.0024 },
    { T: 500, deger: 0.0019 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 115 },
    { T: 200, deger: 108 },
    { T: 375, deger: 92 },
    { T: 404, deger: 58 },
    { T: 480, deger: 60 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 435 },
    { T: 200, deger: 455 },
    { T: 375, deger: 480 },
    { T: 404, deger: 520 },
    { T: 480, deger: 525 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 404, fs: 0.0 },
    { T: 398, fs: 0.15 },
    { T: 392, fs: 0.38 },
    { T: 385, fs: 0.62 },
    { T: 379, fs: 0.85 },
    { T: 375, fs: 1.0 },
  ],
  akiskanlikIndeksi: 92,
  sicakYirtilmaEgilimi: 15,
  gazCozunurlugu: 0.03,
};

const ZA12: IDokumAlasim = {
  id: 'Zn_ZA12',
  ad: 'Cinko ZA-12 (ZnAl11Cu1)',
  adEN: 'Zinc ZA-12 (ZnAl11Cu1)',
  kategori: DokumAlasimKategori.CINKO,
  standart: 'ASTM B669',
  T_solidus: 377,
  T_liquidus: 432,
  T_dokum_min: 470,
  T_dokum_max: 510,
  yogunlukSivi: 5800,
  yogunlukKati: 6030,
  cekmeOrani: 0.022,
  gizliIsi: 130000,
  yuzeyGerilimi: 0.72,
  viskoziteEgrisi: [
    { T: 432, deger: 0.0045 },
    { T: 460, deger: 0.0032 },
    { T: 490, deger: 0.0025 },
    { T: 530, deger: 0.0020 },
  ],
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 116 },
    { T: 200, deger: 110 },
    { T: 377, deger: 95 },
    { T: 432, deger: 60 },
    { T: 510, deger: 62 },
  ],
  ozguIsiEgrisi: [
    { T: 25,  deger: 450 },
    { T: 200, deger: 468 },
    { T: 377, deger: 490 },
    { T: 432, deger: 530 },
    { T: 510, deger: 535 },
  ],
  katilasmaModeli: KatilasmaModeli.SCHEIL,
  katilasmaEgrisi: [
    { T: 432, fs: 0.0 },
    { T: 422, fs: 0.12 },
    { T: 410, fs: 0.30 },
    { T: 398, fs: 0.52 },
    { T: 386, fs: 0.75 },
    { T: 380, fs: 0.92 },
    { T: 377, fs: 1.0 },
  ],
  akiskanlikIndeksi: 88,
  sicakYirtilmaEgilimi: 20,
  gazCozunurlugu: 0.03,
};

// ============================================================================
// 11. KALIP MALZEMELERI
// ============================================================================

const SILIKA_KUM: IKalipMalzeme = {
  id: 'kum_silika',
  ad: 'Silika Kum (SiO2)',
  adEN: 'Silica Sand (SiO2)',
  kategori: KalipMalzemeKategori.KUM,
  yogunluk: 1550,
  isiIletkenlik: 0.80,
  ozguIsi: 1130,
  maxSicaklik: 1650,
  arayuzIsiTransferKatsayisi: 500,
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 0.60 },
    { T: 200,  deger: 0.70 },
    { T: 400,  deger: 0.80 },
    { T: 573,  deger: 0.85 },
    { T: 600,  deger: 0.90 },
    { T: 1000, deger: 1.10 },
    { T: 1400, deger: 1.30 },
  ],
};

const ZIRKON_KUM: IKalipMalzeme = {
  id: 'kum_zirkon',
  ad: 'Zirkon Kum (ZrSiO4)',
  adEN: 'Zircon Sand (ZrSiO4)',
  kategori: KalipMalzemeKategori.KUM,
  yogunluk: 2800,
  isiIletkenlik: 1.50,
  ozguIsi: 622,
  maxSicaklik: 2200,
  arayuzIsiTransferKatsayisi: 800,
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 1.20 },
    { T: 200,  deger: 1.35 },
    { T: 400,  deger: 1.50 },
    { T: 800,  deger: 1.70 },
    { T: 1200, deger: 1.90 },
  ],
};

const KROMAT_KUM: IKalipMalzeme = {
  id: 'kum_kromat',
  ad: 'Kromat Kum (Cr2O3)',
  adEN: 'Chromite Sand (FeCr2O4)',
  kategori: KalipMalzemeKategori.KUM,
  yogunluk: 2700,
  isiIletkenlik: 1.30,
  ozguIsi: 670,
  maxSicaklik: 1900,
  arayuzIsiTransferKatsayisi: 750,
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 1.00 },
    { T: 200,  deger: 1.15 },
    { T: 400,  deger: 1.30 },
    { T: 800,  deger: 1.50 },
    { T: 1200, deger: 1.65 },
  ],
};

const OLIVIN_KUM: IKalipMalzeme = {
  id: 'kum_olivin',
  ad: 'Olivin Kum (MgFeSiO4)',
  adEN: 'Olivine Sand (Mg2SiO4)',
  kategori: KalipMalzemeKategori.KUM,
  yogunluk: 1700,
  isiIletkenlik: 1.00,
  ozguIsi: 1050,
  maxSicaklik: 1800,
  arayuzIsiTransferKatsayisi: 600,
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 0.80 },
    { T: 200,  deger: 0.90 },
    { T: 400,  deger: 1.00 },
    { T: 800,  deger: 1.15 },
    { T: 1200, deger: 1.30 },
  ],
};

const H13_KALIP: IKalipMalzeme = {
  id: 'kalip_h13',
  ad: 'H13 Takim Celigi (Basincli Dokum)',
  adEN: 'H13 Tool Steel (Die Casting)',
  kategori: KalipMalzemeKategori.METAL,
  yogunluk: 7800,
  isiIletkenlik: 28.6,
  ozguIsi: 460,
  maxSicaklik: 650,
  arayuzIsiTransferKatsayisi: 10000,
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 24.5 },
    { T: 200, deger: 26.0 },
    { T: 400, deger: 28.0 },
    { T: 600, deger: 28.6 },
    { T: 650, deger: 28.8 },
  ],
};

const SKD61_KALIP: IKalipMalzeme = {
  id: 'kalip_skd61',
  ad: 'SKD61 Takim Celigi (Basincli Dokum)',
  adEN: 'SKD61 Tool Steel (Die Casting)',
  kategori: KalipMalzemeKategori.METAL,
  yogunluk: 7810,
  isiIletkenlik: 28.0,
  ozguIsi: 460,
  maxSicaklik: 620,
  arayuzIsiTransferKatsayisi: 9500,
  isiIletkenlikEgrisi: [
    { T: 25,  deger: 23.5 },
    { T: 200, deger: 25.5 },
    { T: 400, deger: 27.5 },
    { T: 600, deger: 28.0 },
  ],
};

const SERAMIK_KABUK: IKalipMalzeme = {
  id: 'seramik_kabuk',
  ad: 'Seramik Kabuk (Hassas Dokum)',
  adEN: 'Ceramic Shell (Investment Casting)',
  kategori: KalipMalzemeKategori.SERAMIK,
  yogunluk: 1900,
  isiIletkenlik: 1.20,
  ozguIsi: 900,
  maxSicaklik: 1600,
  arayuzIsiTransferKatsayisi: 300,
  isiIletkenlikEgrisi: [
    { T: 25,   deger: 0.80 },
    { T: 200,  deger: 0.95 },
    { T: 400,  deger: 1.10 },
    { T: 800,  deger: 1.20 },
    { T: 1200, deger: 1.35 },
    { T: 1600, deger: 1.45 },
  ],
};

const ZRO2_KAPLAMA: IKalipMalzeme = {
  id: 'kaplama_zro2',
  ad: 'Zirkonyum Oksit Kaplama (ZrO2)',
  adEN: 'Zirconia Wash Coating (ZrO2)',
  kategori: KalipMalzemeKategori.KAPLAMA,
  yogunluk: 3100,
  isiIletkenlik: 2.0,
  ozguIsi: 560,
  maxSicaklik: 2500,
  arayuzIsiTransferKatsayisi: 400,
};

const BN_KAPLAMA: IKalipMalzeme = {
  id: 'kaplama_bn',
  ad: 'Bor Nitrur Kaplama (BN)',
  adEN: 'Boron Nitride Wash (BN)',
  kategori: KalipMalzemeKategori.KAPLAMA,
  yogunluk: 2100,
  isiIletkenlik: 30.0,
  ozguIsi: 800,
  maxSicaklik: 2000,
  arayuzIsiTransferKatsayisi: 600,
};

// ============================================================================
// 12. KOLEKSIYON & KUTUPHANE CLASS
// ============================================================================

const TÜMP_ALASIMLAR: IDokumAlasim[] = [
  AL_A356, AL_A380, AL_A319, AL_AlSi12,
  GG25, GGG40, GGG60, VERMICULAR,
  GP240GH, G17CrMo55, CF8M,
  CuSn10, CuZn_PIRINC, CuAl_BRONZ,
  AZ91, AM60,
  ZAMAK3, ZAMAK5, ZA8, ZA12,
];

const TUM_KALIP_MALZEMELERI: IKalipMalzeme[] = [
  SILIKA_KUM, ZIRKON_KUM, KROMAT_KUM, OLIVIN_KUM,
  H13_KALIP, SKD61_KALIP,
  SERAMIK_KABUK,
  ZRO2_KAPLAMA, BN_KAPLAMA,
];

export class DokumMalzemeDB {
  private alasimlar: Map<string, IDokumAlasim> = new Map();
  private kaliplar:  Map<string, IKalipMalzeme> = new Map();

  constructor() {
    for (const a of TÜMP_ALASIMLAR)      this.alasimlar.set(a.id, a);
    for (const k of TUM_KALIP_MALZEMELERI) this.kaliplar.set(k.id, k);
  }

  // --- Alloy queries ---

  alasimAl(id: string): IDokumAlasim | undefined {
    return this.alasimlar.get(id);
  }

  tumAlasimlar(): IDokumAlasim[] {
    return Array.from(this.alasimlar.values());
  }

  kategoriAlasimlar(kat: DokumAlasimKategori): IDokumAlasim[] {
    return this.tumAlasimlar().filter(a => a.kategori === kat);
  }

  // --- Mold queries ---

  kalipAl(id: string): IKalipMalzeme | undefined {
    return this.kaliplar.get(id);
  }

  tumKaliplar(): IKalipMalzeme[] {
    return Array.from(this.kaliplar.values());
  }

  kategoriKaliplar(kat: KalipMalzemeKategori): IKalipMalzeme[] {
    return this.tumKaliplar().filter(k => k.kategori === kat);
  }

  // --- Temperature-dependent property access ---

  viskozite(alasimId: string, T: number): number {
    const a = this.alasimlar.get(alasimId);
    return a ? sicaklikLookup(a.viskoziteEgrisi, T) : 0;
  }

  isiIletkenlik(alasimId: string, T: number): number {
    const a = this.alasimlar.get(alasimId);
    return a ? sicaklikLookup(a.isiIletkenlikEgrisi, T) : 0;
  }

  ozguIsi(alasimId: string, T: number): number {
    const a = this.alasimlar.get(alasimId);
    return a ? sicaklikLookup(a.ozguIsiEgrisi, T) : 0;
  }

  katiKesri(alasimId: string, T: number): number {
    const a = this.alasimlar.get(alasimId);
    return a ? katilasmaFraksiyonu(a.katilasmaEgrisi, T) : 0;
  }

  kalipIsiIletkenlik(kalipId: string, T: number): number {
    const k = this.kaliplar.get(kalipId);
    if (!k) return 0;
    if (k.isiIletkenlikEgrisi?.length) return sicaklikLookup(k.isiIletkenlikEgrisi, T);
    return k.isiIletkenlik;
  }

  // --- Effective density interpolation (solid+liquid mush) ---

  efektifYogunluk(alasimId: string, T: number): number {
    const a = this.alasimlar.get(alasimId);
    if (!a) return 0;
    const fs = katilasmaFraksiyonu(a.katilasmaEgrisi, T);
    return a.yogunlukSivi * (1 - fs) + a.yogunlukKati * fs;
  }

  // --- Search ---

  ara(arama: string): IDokumAlasim[] {
    const q = arama.toLowerCase();
    return this.tumAlasimlar().filter(a =>
      a.ad.toLowerCase().includes(q) ||
      a.adEN.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q) ||
      a.standart.toLowerCase().includes(q)
    );
  }
}

// ============================================================================
// 13. SINGLETON
// ============================================================================

let _instance: DokumMalzemeDB | null = null;

export function getDokumMalzemeDB(): DokumMalzemeDB {
  if (!_instance) _instance = new DokumMalzemeDB();
  return _instance;
}
